import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaPushFirmwareModal } from "@src/reducers/your-reducer";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { RootState } from "@src/reducers/store";
import { ListItem } from "@src/types/ota-update";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { createInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/create-installation-process-use-case";
import {
  INSTALL_AND_ACTION_BLOCK,
  OTA_OBJECT_TYPE,
} from "@src/cloud-connect/ota-update/constants";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

export const useOtaPushFirmwareModal = (
  data: ListItem,
  onClose: () => void,
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const [formValues, setFormValues] = useState<{
    targetPath: string;
    username: string;
    password: string;
    privateKeyPassword: string;
    privateKeyFile: File | null;
  }>({
    targetPath: "",
    username: "",
    password: "",
    privateKeyPassword: "",
    privateKeyFile: null,
  });
  const [isAskForApprovalLoading, setIsAskForApprovalLoading] = useState(false);

  // Get machine source data from Redux store
  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice?.authContext,
  );

  const isBackupInstallation = data?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;

  const texts = {
    closePushFirmwareModalAriaLabel: isBackupInstallation
      ? i18n("modules.otaPushFirmwareModal.closePushBackupModalAriaLabel")
      : i18n("modules.otaPushFirmwareModal.closePushFirmwareModalAriaLabel"),
    title: isBackupInstallation
      ? i18n("modules.otaPushFirmwareModal.titleBackup")
      : i18n("modules.otaPushFirmwareModal.titleFirmware"),
    firmwareSectionTitle: isBackupInstallation
      ? i18n("modules.otaPushFirmwareModal.backupSectionTitle")
      : i18n("modules.otaPushFirmwareModal.firmwareSectionTitle"),
    uploadedAtLabel: i18n("modules.otaPushFirmwareModal.uploadedAtLabel"),
    machineSectionTitle: i18n(
      "modules.otaPushFirmwareModal.machineSectionTitle",
    ),
    machineIdLabel: i18n("modules.otaPushFirmwareModal.machineIdLabel"),
    machineIpLabel: i18n("modules.otaPushFirmwareModal.machineIpLabel"),
    foldersSectionTitle: isBackupInstallation
      ? i18n("modules.otaPushFirmwareModal.backupFoldersSectionTitle")
      : i18n("modules.otaPushFirmwareModal.firmwareFoldersSectionTitle"),
    targetPathLabel: i18n("modules.otaPushFirmwareModal.targetPathLabel"),
    targetPathPlaceholder: i18n(
      "modules.otaPushFirmwareModal.targetPathPlaceholder",
    ),
    privateKeyFileForAuthLabel: i18n(
      "modules.otaPushFirmwareModal.privateKeyFileForAuthLabel",
    ),
    privateKeyPasswordLabel: i18n(
      "modules.otaPushFirmwareModal.privateKeyPasswordLabel",
    ),
    privateKeyPasswordPlaceholder: i18n(
      "modules.otaPushFirmwareModal.privateKeyPasswordPlaceholder",
    ),
    usernameLabel: i18n("modules.otaPushFirmwareModal.usernameLabel"),
    usernamePlaceholder: i18n(
      "modules.otaPushFirmwareModal.usernamePlaceholder",
    ),
    passwordLabel: i18n("modules.otaPushFirmwareModal.passwordLabel"),
    passwordPlaceholder: i18n(
      "modules.otaPushFirmwareModal.passwordPlaceholder",
    ),
    showPasswordTooltip: i18n(
      "modules.otaPushFirmwareModal.showPasswordTooltip",
    ),
    hidePasswordTooltip: i18n(
      "modules.otaPushFirmwareModal.hidePasswordTooltip",
    ),
    targetPathLabelWithoutColon: i18n(
      "modules.otaPushFirmwareModal.targetPathLabelWithoutColon",
    ),
    usernameLabelWithoutColon: i18n(
      "modules.otaPushFirmwareModal.usernameLabelWithoutColon",
    ),
    privateKeyFileForAuthLabelWithoutColon: i18n(
      "modules.otaPushFirmwareModal.privateKeyFileForAuthLabelWithoutColon",
    ),
    passwordOrPrivateKeyRequired: i18n(
      "modules.otaPushFirmwareModal.passwordOrPrivateKeyRequired",
    ),
    helperText: isBackupInstallation
      ? i18n("modules.otaPushFirmwareModal.helperTextBackup")
      : i18n("modules.otaPushFirmwareModal.helperTextFirmware"),
    cancelButtonText: i18n("modules.otaPushFirmwareModal.cancelButtonText"),
    askForApprovalButtonText: i18n(
      "modules.otaPushFirmwareModal.askForApprovalButtonText",
    ),
    pleaseFixFields: i18n("modules.otaPushFirmwareModal.pleaseFixFields"),
    failedToCreateInstallationProcess: i18n(
      "modules.otaPushFirmwareModal.failedToCreateInstallationProcess",
    ),
    installationProcessAlreadyExists: i18n(
      "modules.otaPushFirmwareModal.installationProcessAlreadyExists",
    ),
  };

  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const firmware = {
    fileName: data?.filename,
    fileSize: formatBytes(data?.size_bytes),
    uploadedAt: data?.uploaded_at,
  };

  const machine = {
    name: source?.name,
    id: source?.source_id,
    ip: source?.dsn,
  };

  const handleCloseModal = () => {
    onClose();
    dispatch(updateShowOtaPushFirmwareModal(false));
  };

  const handleTargetPathChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, targetPath: value }));
  };

  const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, username: value }));
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, password: value }));
  };

  const handlePrivateKeyFileSelection = (file: File | null) => {
    setFormValues((prev) => ({ ...prev, privateKeyFile: file }));
  };

  const handlePrivateKeyPasswordChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, privateKeyPassword: value }));
  };

  const handleAskForApproval = async () => {
    if (isAskForApprovalLoading) {
      return;
    }

    const missingFields: string[] = [];
    if (formValues.targetPath.trim() === "") {
      missingFields.push(texts.targetPathLabelWithoutColon);
    }
    if (formValues.username.trim() === "") {
      missingFields.push(texts.usernameLabelWithoutColon);
    }

    if (missingFields.length > 0) {
      warningSnackbar(
        `${texts.pleaseFixFields}: ${missingFields.join(", ")}`,
        5000,
      );
      return;
    }

    setIsAskForApprovalLoading(true);

    try {
      const formData = new FormData();
      formData.append("firmware_id", data?.firmware_id);
      formData.append("delivery_type", "SFTP");
      formData.append("target_path", formValues.targetPath);
      formData.append("ssh_username", formValues.username);
      if (formValues.password.trim() !== "") {
        formData.append("ssh_password", formValues.password);
      }
      if (formValues.privateKeyFile) {
        formData.append("private_key", formValues.privateKeyFile);
      }
      if (formValues.privateKeyPassword.trim() !== "") {
        formData.append("keyfile_password", formValues.privateKeyPassword);
      }
      const result = await createInstallationProcessUseCase(formData);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Result of createInstallationProcessUseCase:",
          result,
        );
      if (result.success && result.data?.data?.update_id) {
        const isAdmin =
          authContext?.permissions.includes("initiate-update-process") &&
          authContext?.permissions.includes("approve-update-process");
        isDebugLoggingEnabled &&
          console.log(
            "OTA_DEBUG: Installation process created successfully. Is admin:",
            isAdmin,
          );
        handleCloseModal();
        setInstallAndActionBlock(
          isAdmin
            ? INSTALL_AND_ACTION_BLOCK.REQUEST_APPROVAL_FOR_ADMIN
            : INSTALL_AND_ACTION_BLOCK.PENDING_APPROVAL_BY_OPERATOR,
        );
        return;
      }

      console.error("Failed to create installation process:", result.error);
      if (result.statusCode === 409) {
        warningSnackbar(texts.installationProcessAlreadyExists, 5000);
        handleCloseModal();
        return;
      }

      warningSnackbar(texts.failedToCreateInstallationProcess, 5000);
      setIsAskForApprovalLoading(false);
    } catch (error) {
      console.error("Error creating installation process:", error);
      setIsAskForApprovalLoading(false);
      handleCloseModal();
    }
  };

  return {
    targetPath: formValues.targetPath,
    username: formValues.username,
    password: formValues.password,
    privateKeyPassword: formValues.privateKeyPassword,
    privateKeyFile: formValues.privateKeyFile,
    firmware,
    machine,
    folders: data?.archive_structure_root_level,
    texts,
    handleCloseModal,
    handleTargetPathChange,
    handleUsernameChange,
    handlePasswordChange,
    handlePrivateKeyFileSelection,
    handlePrivateKeyPasswordChange,
    handleAskForApproval,
    isAskForApprovalLoading,
  };
};
